import { useCallback } from "react"

import { toast } from "@coval/ui"

import type { VisualizerBucketAssignment } from "../components/uploadedConversationsVisualizerDataset"

// Matches the @maxItems bound on SimulationOutputIds in
// uploaded_conversation_keyset_request.d.ts -- the clustering-output schema itself has
// no upper bound on a cluster's membership, so a large real cluster must be capped
// before forwarding or the backend rejects the request outright.
export const MAX_CLUSTER_FILTER_SIMULATION_OUTPUT_IDS = 10_000

interface UseUploadedConversationsVisualizerClickForwardingParams {
  bucketAssignments: VisualizerBucketAssignment[]
  isShowingLatestRun: boolean
  onClusterFilterRequest?: (simulationOutputIds: string[]) => void
  onConversationClick?: (conversationId: string) => void
}

/**
 * Fallback mode plots sample data whose ids don't exist in the real conversations
 * table -- forwarding a sample click would filter that table to a real empty result
 * instead of remaining a harmless demo interaction, so both handlers no-op while
 * isShowingLatestRun is false.
 */
export function useUploadedConversationsVisualizerClickForwarding({
  bucketAssignments,
  isShowingLatestRun,
  onClusterFilterRequest,
  onConversationClick,
}: UseUploadedConversationsVisualizerClickForwardingParams) {
  const handleConversationIdsFilter = useCallback(
    (allIds: string[]) => {
      if (!isShowingLatestRun) {
        return
      }
      if (allIds.length === 0) {
        return
      }
      if (allIds.length > MAX_CLUSTER_FILTER_SIMULATION_OUTPUT_IDS) {
        // A truncated id-list filter would silently misrepresent the cluster to the
        // table and anything downstream of it (selection, bulk delete, CSV export), so
        // an oversized bucket is refused outright rather than partially filtered.
        toast.warning({
          title: "Cluster too large to filter",
          description: `This cluster has ${allIds.length.toLocaleString()} conversations, over the ${MAX_CLUSTER_FILTER_SIMULATION_OUTPUT_IDS.toLocaleString()} the table can filter by at once. Narrow the results with another filter first.`,
        })
        return
      }
      onClusterFilterRequest?.(allIds)
    },
    [isShowingLatestRun, onClusterFilterRequest],
  )

  const handleLegendBucketClick = useCallback(
    (bucketKey: string) => {
      const bucketAssignment = bucketAssignments.find(
        (assignment) => assignment.bucketKey === bucketKey,
      )
      if (
        bucketAssignment?.conversations.every(
          (conversation) => conversation.isSelectable !== false,
        )
      ) {
        handleConversationIdsFilter(
          bucketAssignment.conversations.map((conversation) => conversation.id),
        )
      }
    },
    [bucketAssignments, handleConversationIdsFilter],
  )

  const handleConversationClick = useCallback(
    (conversationId: string) => {
      if (!isShowingLatestRun) {
        return
      }
      onConversationClick?.(conversationId)
    },
    [isShowingLatestRun, onConversationClick],
  )

  return {
    handleConversationClick,
    handleConversationIdsFilter,
    handleLegendBucketClick,
  }
}
